/**
 * InstitutionalNavigation.jsx — GRANULLO V2 NAVIGATION LAW
 *
 * Locked structure:
 * Granullo | Product v | Platform v | Pricing | Enter Simulation
 *
 * Hover-first dropdowns (120-180ms delay).
 * World-map dropdown layouts (2-column calm).
 * Signature hover stroke (editorial red underline draw).
 * Hand-drawn expansion glyphs (+ -> - morph).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SignatureExpansionGlyph } from '../motion/EditorialPrimitives';
import { analytics } from '../../lib/analytics';
import CurrencySwitcher from '../common/CurrencySwitcher';

const EDITORIAL_RED = '#CD2200';

/* ═══ SIGNATURE HOVER STROKE (SVG underline draw-in) ═══ */
const SignatureStroke = ({ active }) => (
  <svg
    viewBox="0 0 60 4"
    preserveAspectRatio="none"
    style={{ position: 'absolute', bottom: -2, left: '10%', width: '80%', height: 3, overflow: 'visible' }}
  >
    <motion.path
      d="M0 2 C5 0.5, 12 3.5, 20 2 C28 0.5, 35 3, 42 2 C49 1, 55 2.5, 60 2"
      fill="none"
      stroke={EDITORIAL_RED}
      strokeWidth="1.5"
      strokeLinecap="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={active ? { pathLength: 1, opacity: 0.8 } : { pathLength: 0, opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    />
  </svg>
);

/* ═══ NAV LINK WITH SIGNATURE STROKE ═══ */
const NavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={to}
      className="relative text-[13px] transition-all py-2 px-2.5"
      style={{
        color: isActive ? 'var(--shell-text)' : 'var(--shell-text-secondary)',
        fontWeight: isActive ? 500 : 400,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`nav-${children.toString().toLowerCase().replace(/\s+/g, '-')}`}
    >
      {children}
      <SignatureStroke active={hovered || isActive} />
    </Link>
  );
};

/* ═══ PRODUCT ▼ — The conviction case (Why → What → How → Proof → Security) ═══ */
const PRODUCT_ITEMS = [
  { to: '/what-granullo-is',    label: 'What Granullo Is',     desc: 'The system that decides before money moves' },
  { to: '/how-granullo-works',  label: 'How It Works',          desc: 'Intent → Conditions → Allocation → Proof → Release' },
  { to: '/historical-timeline', label: 'The Problem',           desc: 'A century of the same failure under new names' },
  { to: '/proof',               label: 'Proof Journey',         desc: 'Guided institutional proof in 9 steps' },
  { to: '/trust',               label: 'Security',              desc: 'How we protect institutional trust' },
];

/* ═══ PLATFORM ▼ — The product surface (pattern → fabric → signals → playbook → labs) ═══ */
const PLATFORM_ITEMS = [
  { to: '/#pattern-library',      label: 'Pattern Library',       desc: 'Eight universal transaction structures' },
  { to: '/data-fabric',           label: 'Data Fabric',           desc: 'Governed objects and ontology surface' },
  { to: '/signals',               label: 'Signals',               desc: 'Live visual trust observatory' },
  { to: '/playbook',              label: 'Playbook',              desc: 'The fastest path to first confidence' },
  { to: '/launch-observability',  label: 'Launch Observability',  desc: 'What regulators and boards actually see' },
  { to: '/labs',                  label: 'Labs',                  desc: 'Governance experimentation sandbox' },
];

/* ═══ INTELLIGENCE ▼ — Zero-LLM cognition layer ═══ */
const INTELLIGENCE_ITEMS = [
  { to: '/intelligence',                          label: 'Overview',              desc: 'Live cognition — paste, extract, infer, recommend' },
  { to: '/ask-intelligence',                      label: 'Ask Intelligence',      desc: 'Ask about any trust signal or decision' },
  { to: '/intelligence/capital-with-coordinates', label: 'Capital Coordinates',   desc: 'Mission-driven capital routing atlas' },
  { to: '/intelligence/discover',                 label: 'Discover',              desc: 'Cold-start world understanding in seconds' },
  { to: '/intelligence/memory',                   label: 'Memory',                desc: 'Twin inheritance and trust compounding' },
  { to: '/intelligence/world-models',             label: 'World Models',          desc: 'How Granullo generalizes across ecosystems' },
  { to: '/intelligence/how-it-thinks',            label: 'How It Thinks',         desc: 'Ontology, confidence scoring, proof prediction' },
];

/* ═══ PARTNERS ▼ — Banking rails / execution partners ═══ */
const PARTNER_ITEMS = [
  { to: '/partners?from=nav',     label: 'Partner Overview',      desc: 'Why execution partners integrate with Granullo' },
  { to: '/partners/integrate',    label: 'Integrate',             desc: 'API reference, webhooks, SDKs — self-serve hub' },
  { to: '/gtm/rail-narrative',    label: 'Rail Partner Narrative', desc: 'The partnership story — what we build together' },
  { to: '/gtm/sponsor-bank',      label: 'Sponsor Bank Playbook', desc: 'For regulated banking partners — scope + shape' },
  { to: '/gtm/one-pager',         label: 'One-Pager',             desc: 'The partnership at a glance' },
  { to: '/gtm/case-studies',      label: 'Case Studies',          desc: 'Live pilots and real blocked transactions' },
  { to: '/partner',               label: 'Partner Portal',        desc: 'Active partners — sign in' },
];

/* ═══ PRICING ▼ — Commercial surface ═══ */
const PRICING_ITEMS = [
  { to: '/pricing',               label: 'Pricing',               desc: 'Structural governance — 0.50% of governed program value' },
  { to: '/estimator',             label: 'Misuse Prevention Estimator', desc: 'Quantify what Granullo would have prevented' },
  { to: '/enterprise',            label: 'Enterprise',            desc: 'Volume pricing for programs over $500M AAV' },
];

/* ═══ PROGRAMS ▼ — Real, distinct pages — no #anchor stacking ═══ */
const PROGRAMS_ITEMS = [
  { to: '/programs/pilots',                label: 'World Pilot Readiness',     desc: 'Catalog of all 4 deployable pilot packs' },
  { to: '/programs/needs-discovery',       label: 'Live Needs Discovery',      desc: 'You set the priorities; we surface ranked matches' },
  { to: '/foundations',                    label: 'Foundation Network',        desc: 'Granullo Partner / Governed / External — the foundations Granullo trusts' },
  { to: '/programs/merchant-network',      label: 'Governed Merchant Network', desc: 'Where money is allowed to move — trust-scored merchants + approved paths' },
  { to: '/programs/csr-foundations',       label: 'CSR & Foundations Pack',    desc: 'Conditional disbursement workflows for foundations & CSR teams' },
  { to: '/programs/trade-corridor',        label: 'Trade Corridor Pack',       desc: 'Cross-border release-on-arrival for shippers & freight finance' },
  { to: '/programs/enterprise-finance',    label: 'Enterprise Finance Pack',   desc: 'AR/AP, vendor payouts, milestone-gated treasury flows' },
  { to: '/programs/pos-misuse',            label: 'POS Misuse Prevention',     desc: 'MCC-governed merchant settlement for retail & banking' },
  { to: '/impact-intelligence',            label: 'Impact Intelligence',       desc: 'Verified causes, alerts, and partner organizations (read-only)' },
];

/* ═══ HOVER-FIRST DROPDOWN ═══ */
const HoverDropdown = ({ label, items, testId, pathPrefix }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const location = useLocation();
  const ref = useRef(null);
  const enterTimer = useRef(null);
  const exitTimer = useRef(null);
  const isActive = items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'));

  const handleEnter = useCallback(() => {
    clearTimeout(exitTimer.current);
    enterTimer.current = setTimeout(() => setOpen(true), 140);
    setHovered(true);
  }, []);

  const handleLeave = useCallback(() => {
    clearTimeout(enterTimer.current);
    exitTimer.current = setTimeout(() => {
      setOpen(false);
      setHovered(false);
    }, 160);
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(enterTimer.current);
      clearTimeout(exitTimer.current);
    };
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave} data-testid={testId}>
      <button
        className="relative flex items-center gap-0.5 text-[13px] transition-all py-2 px-2.5 cursor-pointer"
        style={{
        color: isActive || hovered ? 'var(--shell-text)' : 'var(--shell-text-secondary)',
          fontWeight: isActive ? 500 : 400,
        }}
        data-testid={`nav-${label.toLowerCase()}`}
      >
        {label}
        <span className="ml-1"><SignatureExpansionGlyph open={open} size={11} /></span>
        <SignatureStroke active={hovered || isActive} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute left-0 top-full mt-2 z-50 overflow-hidden"
            style={{
              background: 'var(--shell-surface)',
              border: '1px solid var(--shell-border-exhibit)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              borderRadius: 8,
              minWidth: 320,
            }}
            data-testid={`${testId}-menu`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 p-2">
              {items.map((item) => {
                const isItemActive = location.pathname === item.to;
                const isHashLink = item.to.includes('#');
                const handleItemClick = (e) => {
                  setOpen(false);
                  if (isHashLink) {
                    e.preventDefault();
                    const [pathname, hash] = item.to.split('#');
                    const targetPath = pathname || '/';
                    // If we're already on the target path, just scroll; otherwise navigate + scroll
                    if (location.pathname === targetPath) {
                      const el = document.getElementById(hash);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      // Navigate and then scroll once DOM mounts
                      window.location.href = `${targetPath}#${hash}`;
                    }
                  }
                };
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={handleItemClick}
                    className="group rounded-lg px-3 py-3 transition-all duration-150"
                    style={{ background: isItemActive ? 'var(--shell-bg-alt)' : 'transparent' }}
                    onMouseEnter={e => { if (!isItemActive) e.currentTarget.style.background = 'var(--shell-bg-alt)'; }}
                    onMouseLeave={e => { if (!isItemActive) e.currentTarget.style.background = 'transparent'; }}
                    data-testid={`dropdown-link-${item.to.split('/').pop().replace('#', 'hash-') || 'root'}`}
                  >
                    <span className="text-[12px] font-medium block" style={{ color: isItemActive ? 'var(--shell-text)' : 'var(--shell-text-secondary)' }}>
                      {item.label}
                    </span>
                    <span className="text-[10px] block mt-0.5" style={{ color: 'var(--shell-text-faint)', lineHeight: 1.4 }}>
                      {item.desc}
                    </span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══ MAIN NAVIGATION (inline nav for use inside header wrapper) ═══ */
const InstitutionalNavigation = ({ standalone = true }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!standalone) {
    return (
      <nav className="flex items-center gap-0.5" data-testid="institutional-navigation">
        <HoverDropdown label="Product" items={PRODUCT_ITEMS} testId="product-dropdown" />
        <HoverDropdown label="Platform" items={PLATFORM_ITEMS} testId="platform-dropdown" />
        <HoverDropdown label="Programs" items={PROGRAMS_ITEMS} testId="programs-dropdown" />
        <HoverDropdown label="Intelligence" items={INTELLIGENCE_ITEMS} testId="intelligence-dropdown" />
        <HoverDropdown label="Partners" items={PARTNER_ITEMS} testId="partners-dropdown" />
        <HoverDropdown label="Pricing" items={PRICING_ITEMS} testId="pricing-dropdown" />
        <CurrencySwitcher compact />
        <Link to="/simulate" className="ed-nav-cta ml-2" data-testid="nav-enter-simulation">Enter Simulation</Link>
      </nav>
    );
  }

  return (
    <>
      <header className="ed-nav fixed top-0 left-0 right-0 z-50" data-testid="institutional-navigation">
        <div className="max-w-[1280px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-[15px] font-semibold" style={{ fontFamily: '"Inter Tight", sans-serif', color: 'var(--shell-text)', letterSpacing: '-0.03em' }} data-testid="nav-wordmark">Granullo</Link>
          <nav className="hidden md:flex items-center gap-0.5">
            <HoverDropdown label="Product" items={PRODUCT_ITEMS} testId="product-dropdown" />
            <HoverDropdown label="Platform" items={PLATFORM_ITEMS} testId="platform-dropdown" />
            <HoverDropdown label="Programs" items={PROGRAMS_ITEMS} testId="programs-dropdown" />
            <HoverDropdown label="Intelligence" items={INTELLIGENCE_ITEMS} testId="intelligence-dropdown" />
            <HoverDropdown label="Partners" items={PARTNER_ITEMS} testId="partners-dropdown" />
            <HoverDropdown label="Pricing" items={PRICING_ITEMS} testId="pricing-dropdown" />
            <CurrencySwitcher compact />
            <Link to="/simulate" onClick={() => analytics.navCtaClick('enter_simulation', window.location.pathname)} className="ed-nav-cta ml-2" data-testid="nav-enter-simulation">Enter Simulation</Link>
          </nav>
          <InstitutionalMobileMenuButton isOpen={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)} />
        </div>
      </header>
      <InstitutionalMobileNavigation isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
};

/* ═══ MOBILE NAVIGATION ═══ */
const InstitutionalMobileNavigation = ({ isOpen, onClose }) => {
  const NAV_LINKS = [
    { type: 'divider', label: 'Product' },
    { to: '/what-granullo-is',    label: 'What Granullo Is' },
    { to: '/how-granullo-works',  label: 'How It Works' },
    { to: '/historical-timeline', label: 'The Problem' },
    { to: '/proof',               label: 'Proof Journey' },
    { to: '/trust',               label: 'Security' },
    { type: 'divider', label: 'Platform' },
    { to: '/#pattern-library',    label: 'Pattern Library' },
    { to: '/data-fabric',         label: 'Data Fabric' },
    { to: '/signals',             label: 'Signals' },
    { to: '/playbook',            label: 'Playbook' },
    { to: '/launch-observability', label: 'Launch Observability' },
    { to: '/labs',                label: 'Labs' },
    { type: 'divider', label: 'Programs' },
    { to: '/programs/pilots',             label: 'World Pilot Readiness' },
    { to: '/programs/needs-discovery',    label: 'Live Needs Discovery' },
    { to: '/foundations',                 label: 'Foundation Network' },
    { to: '/programs/merchant-network',   label: 'Governed Merchant Network' },
    { to: '/programs/csr-foundations',    label: 'CSR & Foundations Pack' },
    { to: '/programs/trade-corridor',     label: 'Trade Corridor Pack' },
    { to: '/programs/enterprise-finance', label: 'Enterprise Finance Pack' },
    { to: '/programs/pos-misuse',         label: 'POS Misuse Prevention' },
    { to: '/impact-intelligence',         label: 'Impact Intelligence' },
    { type: 'divider', label: 'Intelligence' },
    { to: '/intelligence',                          label: 'Overview' },
    { to: '/ask-intelligence',                      label: 'Ask Intelligence' },
    { to: '/intelligence/capital-with-coordinates', label: 'Capital Coordinates' },
    { to: '/intelligence/discover',                 label: 'Discover' },
    { to: '/intelligence/memory',                   label: 'Memory' },
    { to: '/intelligence/world-models',             label: 'World Models' },
    { to: '/intelligence/how-it-thinks',            label: 'How It Thinks' },
    { type: 'divider', label: 'Partners' },
    { to: '/partners',            label: 'Partner Overview' },
    { to: '/gtm/rail-narrative',  label: 'Rail Partner Narrative' },
    { to: '/gtm/sponsor-bank',    label: 'Sponsor Bank Playbook' },
    { to: '/gtm/one-pager',       label: 'One-Pager' },
    { to: '/gtm/case-studies',    label: 'Case Studies' },
    { to: '/partner',             label: 'Partner Portal' },
    { type: 'divider', label: 'Pricing' },
    { to: '/pricing',             label: 'Pricing' },
    { to: '/estimator',           label: 'Misuse Prevention Estimator' },
    { to: '/enterprise',          label: 'Enterprise' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="fixed right-0 top-0 bottom-0 w-[280px] z-50 overflow-y-auto" style={{ background: 'var(--g-surface-solid)', boxShadow: 'var(--g-shadow-high)' }} data-testid="mobile-nav-panel">
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--g-border)' }}>
              <span className="text-[15px] font-semibold" style={{ color: 'var(--g-text)' }}>Menu</span>
              <button onClick={onClose} className="p-2 rounded-lg" aria-label="Close menu">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--g-text)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-1">
              {NAV_LINKS.map((link, i) => {
                if (link.type === 'divider') {
                  return <div key={i} className="pt-3 pb-1" style={{ borderTop: i > 0 ? '1px solid var(--g-border)' : 'none' }}>
                    {link.label && <span className="text-[10px] font-mono uppercase tracking-[0.15em]" style={{ color: 'var(--g-muted-2)' }}>{link.label}</span>}
                  </div>;
                }
                return (
                  <Link key={link.to} to={link.to} onClick={onClose} className="block p-3 rounded-lg transition-colors" style={{ color: 'var(--g-text)' }} data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <span className="text-[14px] font-medium">{link.label}</span>
                  </Link>
                );
              })}
              <div className="pt-4 mt-4 space-y-2" style={{ borderTop: '1px solid var(--g-border)' }}>
                <Link to="/simulate" onClick={onClose} className="block w-full text-center p-3 text-[14px] font-medium rounded-lg" style={{ background: 'var(--g-text)', color: 'var(--g-bg)' }} data-testid="mobile-simulation-room">
                  Enter Simulation
                </Link>
                <Link to="/login" onClick={onClose} className="block w-full text-center p-3 text-[14px] font-medium rounded-lg" style={{ border: '1px solid var(--g-border)', color: 'var(--g-text)' }} data-testid="mobile-sign-in">
                  Sign In
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const InstitutionalMobileMenuButton = ({ isOpen, onClick }) => {
  return (
    <button onClick={onClick} className="md:hidden p-2 rounded-lg transition-colors" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-testid="mobile-menu-button">
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--g-text)' }}>
        {isOpen ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        )}
      </svg>
    </button>
  );
};

export {
  InstitutionalNavigation,
  InstitutionalMobileNavigation,
  InstitutionalMobileMenuButton,
  NavLink
};
export default InstitutionalNavigation;
